import { LocalDate } from "@flockcover/flock-svc-core-types/lib/values/LocalDate"
import { Dialog } from "@shared/primitive-components"
import { sanitiseDate } from "@shared/utils/formatters/dates"
import { useCallback, useMemo } from "react"
import { FleetEntry } from "../../api/fleet"
import { useAccess, usePolicyAndFleet } from "../../contexts"
import { useInternalFlags } from "../../hooks"
import { ConfirmFleetEntryChanges } from "../confirmChanges/ConfirmFleetEntryChanges"
import { HAVEN_ID } from "../../const/policies"
import { FleetListData } from "../list/fleetListHandlers"
import { EditCoverDates } from "./EditCoverDates"
import { MakeUpdateToFleet } from "./api/MakeUpdateToFleet"
import { useEditCoverDates } from "./hooks/useEditCoverDates"

export const EDIT_COVER_DATES_DATA_TEST_IDS = {
  DIALOG: `editCoverDatesDialog`,
  NEXT_BUTTON: `editCoverDatesNextButton`,
  BACK_BUTTON: `editCoverDatesBackButton`,
}
export interface EditCoverDatesModalProps {
  selectedRows: ReadonlyArray<FleetListData>
  onDismiss: () => void
  onUpdateComplete: () => void
}

const correctFormat = (date: string) => (date.includes(`-`) ? date : sanitiseDate(date)) as LocalDate

// @HACK
// This is needed because the offCoverDate isnt always in the same format.
// Sometimes appearing as dd.MM.yyyy or yyyy-MM-dd. The date is probably being cast somewhere and needs
// addressing in another PR.
// Prefered return format is yyyy-MM-dd as this is how the backend accepts it
const sanitiseRows = (selectedRows: ReadonlyArray<FleetListData>): ReadonlyArray<FleetEntry> =>
  selectedRows.map((row) => ({
    ...row,
    onCoverDate: correctFormat(row.onCoverDate),
    offCoverDate: row.offCoverDate === null ? null : correctFormat(row.offCoverDate),
    sentToMID: row.sentToMID as LocalDate,
  }))

export const EditCoverDatesModal = ({ selectedRows, onUpdateComplete, onDismiss }: EditCoverDatesModalProps) => {
  const { flags } = useInternalFlags()
  const { policy } = usePolicyAndFleet()
  const isHavenPolicy = policy.underwriter.id === HAVEN_ID
  const isCourier = policy.meta?.segment === `courier`

  const rowsToEdit = useMemo(() => sanitiseRows(selectedRows), [selectedRows])

  const hasAdminAccess = useAccess().hasAdminAccess()
  const {
    errors,
    displayAddedInErrorCheckbox,
    status,
    changedEntries,
    unchangedEntries,
    primaryModalButtonDisabled,
    editFormState,
    editIdSet,
    editedEntries,
    dispatchEditForm,
    onFinishedEditing,
    onCancelConfirm,
    onConfirm,
  } = useEditCoverDates(
    { startTime: policy.startTime, endTime: policy.endTime },
    hasAdminAccess,
    rowsToEdit,
    isHavenPolicy,
    isCourier
  )

  // Cancelling edits and closing modal
  const onCancel = useCallback(() => {
    onDismiss()
    // We would normally reset the hook here
    // date picker does not allow value to be reset. So for now we un-render to reset
  }, [onDismiss])

  return (
    <Dialog
      title="Update cover dates"
      open
      closeDialog={onCancel}
      dataTestId={status === `editing` ? EDIT_COVER_DATES_DATA_TEST_IDS.DIALOG : ``}
      primaryButton={{
        text: status === `editing` ? `Next` : `Apply changes`,
        disabled: primaryModalButtonDisabled || (status === `confirming` && flags.isDemoModeEnabled),
        action: status === `editing` ? onFinishedEditing : onConfirm,
        dataTestID: status === `editing` ? EDIT_COVER_DATES_DATA_TEST_IDS.NEXT_BUTTON : undefined,
      }}
      secondaryButton={status === `editing` ? undefined : { text: `Back`, action: onCancelConfirm }}
    >
      {status === `editing` && (
        <EditCoverDates
          {...{
            state: editFormState,
            dispatch: dispatchEditForm,
            displayAddedInErrorCheckbox,
            numVehicles: editIdSet.size,
            errors,
          }}
        />
      )}
      {status === `confirming` && (
        <ConfirmFleetEntryChanges
          isInsideModal
          {...{
            changedEntries,
            unchangedEntries,
            displayAddedInErrorWarning: editFormState.addedInError,
          }}
        />
      )}
      {status === `confirmed` && (
        <MakeUpdateToFleet
          entries={editedEntries}
          editIdSet={editIdSet}
          addedInError={editFormState.addedInError}
          onUpdateComplete={onUpdateComplete}
        />
      )}
    </Dialog>
  )
}
